// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import translate from "@insite/client-framework/Translate";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { SelectPresentationProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import submitPageFeedback from "@steiner/App/Handlers/ImprovePage/SubmitPageFeedback";
import { GridContainerProps } from "@steiner/MobiusOverrides/GridContainer/GridContainer";
import Select from "@steiner/MobiusOverrides/Select/Select";
import TextArea, { TextAreaPresentationProps } from "@steiner/MobiusOverrides/TextArea/TextArea";
import TextField, { TextFieldPresentationProps } from "@steiner/MobiusOverrides/TextField";
import { useValidate } from "@steiner/XDL/Utilities";
import * as React from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    closeModal: () => void;
    extendedStyles?: ImprovePageLinkModalStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState) => {
    // TODO: fetch enable recaptcha custom settings from BE

    const billToState = getBillToState(state, getSession(state).billToId).value;
    const session = getSession(state);
    return {
        firstName: session?.firstName,
        lastName: session?.lastName,
        phone: billToState?.phone,
        email: session?.email,
    };
};

const mapDispatchToProps = {
    submitPageFeedback,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: React.RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
};

export type ImprovePageFormFields = {
    phone?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    pageUrl?: string;
    requestType?: string[];
    description: string;
    ReCaptchaToken: string;
};

export interface ImprovePageLinkModalStyles {
    form?: InjectableCss;
    formGridContainer?: GridContainerProps;
    fullWidthGridItem?: GridItemProps;
    firstNameTextField?: TextFieldPresentationProps;
    lastNameTextField?: TextFieldPresentationProps;
    emailTextField?: TextFieldPresentationProps;
    phoneNumberTextField?: TextFieldPresentationProps;
    pageUrlTextField?: TextFieldPresentationProps;
    requestTypeSelect?: SelectPresentationProps;
    descriptionTextArea?: TextAreaPresentationProps;
    submitButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    actionButtonsGridItem?: GridItemProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    topMessageText?: TypographyPresentationProps;
}

export const improvePageLinkModalStyles: ImprovePageLinkModalStyles = {};
const StyledForm = getStyledWrapper("form");

const ImprovePageLinkModal: React.FC<Props> = ({
    closeModal,
    submitPageFeedback,
    validateReCaptcha,
    extendedStyles,
    firstName,
    lastName,
    phone,
    email,
}: Props) => {
    const toasterContext = React.useContext(ToasterContext);
    const [description, setDescription] = React.useState("");
    const [styles] = React.useState(() => mergeToNew(improvePageLinkModalStyles, extendedStyles));
    const recaptchaRef: React.RefObject<ReCAPTCHA | null> = React.createRef();

    const descriptionChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDescription(event.target.value);
    };

    const fullUrlWithoutProtocol = window.location.href.replace(/^https?:\/\//, "");
    const submitHandler = async (fieldValues: ImprovePageFormFields, setSubmitting: any) => {
        let recaptchaToken = "";
        const isReCaptchaValid = await validateReCaptcha({ location: "ImprovePage", recaptchaRef });

        if (isReCaptchaValid) {
            // Get latest reCaptcha Token from cookies after its validated to get the one in the modal/form
            recaptchaToken = getCookie("g-recaptcha-response") || "";
        } else {
            return;
        }
        setSubmitting(true);

        submitPageFeedback({
            ...fieldValues,
            description,
            ReCaptchaToken: recaptchaToken || "",
            onSuccess: () => {
                toasterContext.addToast({
                    body: translate("Feedback has been submitted successfully"),
                    messageType: "success",
                });
                setSubmitting(false);
                closeModal();
            },
            onError: (errorMessage: string) => {
                toasterContext.addToast({ body: translate(errorMessage), messageType: "danger" });
                setSubmitting(false);
            },
        });
    };
    const { field, handleSubmit, isSubmitting, values, errors } = useValidate({
        initialValues: {
            firstName: firstName || "",
            lastName: lastName || "",
            email: email || "",
            phone: "",
            pageUrl: fullUrlWithoutProtocol,
            requestType: "",
        },
        validators: {
            firstName: "required|firstName|maxlength:75",
            lastName: "required|lastName|maxlength:75",
            email: "required|email",
            phone: "phone",
            requestType: "required",
        },
        enableReinitialize: true,
        onSubmit: (fieldValues: ImprovePageFormFields, { setSubmitting }: any) => {
            submitHandler(fieldValues, setSubmitting);
        },
    });

    // TODO: change into System list
    const requestType = [
        "Attributes",
        "Availability",
        "Description",
        "Image",
        "Manufacturing Resources",
        "Missing Products",
        "Other",
        "Pricing",
        "Product Category Placement",
        "Related Products",
        "Substitute Products",
        "Unit of Measure",
        "Video",
    ];
    const hasChanges =
        values.firstName !== "" && values.lastName !== "" && values.requestType !== "" && values.email !== "";
    return (
        <StyledForm {...styles.form} onSubmit={handleSubmit} noValidate data-test-selector="improvePageModal">
            <Typography {...styles.topMessageText}>
                {translate(
                    "Use this form to contact us with your suggestions for improvements to the content or operation of this web page",
                )}
            </Typography>
            <GridContainer>
                <GridItem {...styles.fullWidthGridItem}>
                    <TextField
                        {...styles.firstNameTextField}
                        label={translate("First Name")}
                        placeholder={translate("i.e Jon")}
                        {...field("firstName")}
                        data-test-selector="improvePageModal_firstName"
                        required={true}
                        error={field("firstName").error && "First name is a required field"}
                    />
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    <TextField
                        {...styles.lastNameTextField}
                        label={translate("Last Name")}
                        placeholder={translate("i.e Sandler")}
                        {...field("lastName")}
                        required={true}
                        data-test-selector="improvePageModal_lastName"
                        error={field("lastName").error && "Last name is a required field"}
                    />
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    <TextField
                        {...styles.emailTextField}
                        label={translate("Email")}
                        placeholder={translate("email@company.com")}
                        {...field("email")}
                        required={true}
                        data-test-selector="improvePageModal_email"
                        error={field("email").error && "Email is a required field and must be in correct format"}
                    />
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    <TextField
                        {...styles.phoneNumberTextField}
                        label={translate("Phone Number")}
                        placeholder={translate("i.e 123-456-789")}
                        {...field("phone")}
                        data-test-selector="improvePageModal_phoneNumber"
                        error={field("phone").error && "Phone number is a required field"}
                    />
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    <TextField
                        {...styles.pageUrlTextField}
                        label={translate("Page Url")}
                        {...field("pageUrl")}
                        disabled={true}
                        data-test-selector="improvePageModal_pageUrl"
                        error={field("pageUrl").error && "Page Url is a required field"}
                    />
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    <Select
                        {...styles.requestTypeSelect}
                        label={translate("Request Type")}
                        placeholder={translate("Select an option")}
                        {...field("requestType")}
                        required={true}
                        data-test-selector="improvePageModal_companyTypeSelect"
                        error={field("requestType").error && "Request type is a required field"}
                    >
                        <option value="">{translate("Select an option")}</option>
                        {requestType?.map(type => (
                            <option key={type} value={type}>
                                {type}
                            </option>
                        ))}
                    </Select>
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    {/* TextArea has its own validation and change handler because isnt supported in xUseValidate */}
                    <TextArea
                        {...styles.descriptionTextArea}
                        label={translate("Description")}
                        placeholder={translate("description")}
                        value={description}
                        onChange={descriptionChangeHandler}
                        required={true}
                        maxLength={1000}
                        data-test-selector="improvePageModal_description"
                    />
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    <ReCaptcha recaptchaRef={recaptchaRef} location="ImprovePage" />
                </GridItem>

                <GridItem {...styles.actionButtonsGridItem}>
                    <Button
                        data-test-selector="improvePageModal_cancelButton"
                        disabled={isSubmitting}
                        onClick={closeModal}
                        {...styles.cancelButton}
                    >
                        {translate("Cancel")}
                    </Button>
                    <Button
                        data-test-selector="improvePageModal_submitButton"
                        type="submit"
                        disabled={!hasChanges}
                        {...styles.submitButton}
                    >
                        {translate("Submit")}
                    </Button>
                </GridItem>
            </GridContainer>
        </StyledForm>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ImprovePageLinkModal);
